import { useRef, useState } from 'react';
import type { Eu } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  ROTULO_DO_AJUSTE,
  concluir,
  conferirPrateleira,
  contarEstoque,
  dataCurta,
  decidirAjuste,
  hora,
  registrarAjusteNoHos,
} from '../../auditoria';
import type {
  CasoCompleto,
  DesfechoDoCaso,
  EtapasDoCaso,
  ResultadoDaAuditoria,
  ResultadoDaConclusao,
} from '../../auditoria';
import type { Escrita } from '../../giro';
import { leituraDasContagens, novaChave, sinalDe } from './etapas';

/**
 * Divergência simples (dono, 13/10/2026) — as 3 etapas num cartão só:
 *   1 · PRATELEIRA (em cima) e 2 · ESTOQUE (embaixo), contadas por quem chegar primeiro, em
 *   qualquer ordem, e vistas por todo mundo na hora (a tela relê o caso sozinha);
 *   3 · DECISÃO: com as duas contagens, quem decide marca "foi erro do balconista" ou "não foi".
 * Toda escrita: botão travado enquanto envia, uma `chave` por AÇÃO (a mesma no reenvio, para o
 * servidor não duplicar) e nada de "sucesso" sem a confirmação do servidor.
 */

const unidades = (n: number) => `${String(n)} ${n === 1 ? 'unidade' : 'unidades'}`;
const numOuTraco = (n: number | null) => (n === null ? '—' : String(n));
const quando = (iso: string) => `${dataCurta(iso)} às ${hora(iso)}`;
const primeiro = (nome: string | null) => (nome ?? '').split(' ')[0] ?? '';
const soDigitos = (v: string) => v.replace(/\D/g, '').slice(0, 5);

export type AvisoDaEtapa =
  | { readonly tipo: 'conflito'; readonly texto: string }
  | { readonly tipo: 'incerta'; readonly titulo: string }
  | { readonly tipo: 'recusada'; readonly titulo: string; readonly texto: string }
  | { readonly tipo: 'ok'; readonly titulo: string; readonly texto?: string };

export interface PropsDaEtapa {
  readonly caso: CasoCompleto;
  readonly etapas: EtapasDoCaso;
  readonly sessao: Sessao;
  readonly eu: Eu | null;
  readonly setAviso: (a: AvisoDaEtapa | null) => void;
  /** Os três desfechos; `true` no 3º argumento trata todo 409 como "recarregue o caso". */
  readonly tratar: <T>(r: Escrita<T>, oQue: string, todo409?: boolean) => T | null;
  readonly carregar: () => Promise<void>;
  /** A conclusão confirmada pelo servidor (a tela mostra o fechamento e o próximo caso). */
  readonly aoConcluir: (r: ResultadoDaConclusao) => void;
}

/** Uma chave por ação: muda quando o conteúdo (ou a versão) muda, igual no reenvio. */
function useChaveDaAcao() {
  const ref = useRef<{ assinatura: string; chave: string } | null>(null);
  return (assinatura: string) => {
    if (ref.current?.assinatura !== assinatura) ref.current = { assinatura, chave: novaChave() };
    return ref.current.chave;
  };
}

/* ------------------------------------------------------------------ *
 * Uma contagem (prateleira ou estoque): o número de quem contou e o campo de quem pode contar
 * ------------------------------------------------------------------ */

function Contagem({
  numero,
  titulo,
  onde,
  registro,
  pode,
  naoVerificou,
  aoSalvar,
}: {
  numero: number;
  titulo: string;
  onde: string;
  registro: { valor: string; pessoa: string | null; em: string } | null;
  pode: boolean;
  /** Só no estoque: "não consegui verificar". */
  naoVerificou?: boolean;
  aoSalvar: (n: number | null) => Promise<boolean>;
}) {
  const [qtd, setQtd] = useState<number | null>(null);
  const [editando, setEditando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const salvar = async (n: number | null) => {
    setEnviando(true);
    const ok = await aoSalvar(n);
    setEnviando(false);
    if (ok) {
      setQtd(null);
      setEditando(false);
    }
  };
  const mostraCampo = pode && (registro === null || editando);
  return (
    <div className={`ds-contagem${registro ? ' ds-feita' : ''}`}>
      <div className="ds-topo">
        <span className="ds-etapa">{numero}</span>
        <div>
          <b>{titulo}</b>
          <small>{onde}</small>
        </div>
      </div>
      {registro ? (
        <div className="ds-valor">
          <span className="ds-valor-n">{registro.valor}</span>
          <small>
            {primeiro(registro.pessoa) || 'Conferente'} · {quando(registro.em)}
          </small>
          {pode && !editando && (
            <button type="button" className="cz-link-botao" onClick={() => setEditando(true)}>
              Corrigir
            </button>
          )}
        </div>
      ) : (
        !pode && <p className="ds-esperando">Esperando a contagem.</p>
      )}
      {mostraCampo && (
        <div className="ds-campo">
          <input
            className="et-numero"
            inputMode="numeric"
            placeholder="Quantos?"
            aria-label={`Quantidade ${titulo.toLowerCase()}`}
            value={qtd === null ? '' : String(qtd)}
            onChange={(e) => {
              const v = soDigitos(e.target.value);
              setQtd(v === '' ? null : Number(v));
            }}
          />
          <button
            type="button"
            className="cz-botao cz-botao-primario cz-botao-m"
            disabled={qtd === null || enviando}
            onClick={() => void salvar(qtd)}
          >
            {enviando ? 'Salvando…' : 'Salvar'}
          </button>
          {naoVerificou && (
            <button
              type="button"
              className="cz-link-botao"
              disabled={enviando}
              onClick={() => void salvar(null)}
            >
              Não consegui verificar
            </button>
          )}
          {editando && (
            <button type="button" className="cz-link-botao" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * O cartão do caso: resumo + 1 prateleira + 2 estoque + 3 decisão
 * ------------------------------------------------------------------ */

export function ConferenciaSimples(p: PropsDaEtapa) {
  const { caso, etapas, sessao, eu, tratar, carregar, setAviso, aoConcluir } = p;
  const a = etapas.abertura;
  const inconsistente = caso.deEstoque === true;
  const prat =
    [...etapas.prateleira].reverse().find((x) => x.rodada === etapas.rodada && x.liberouPara) ??
    null;
  const est = [...etapas.estoque].reverse().find((x) => x.rodada === etapas.rodada) ?? null;
  const totalDoEstoque = est?.modo === 'TOTAL' ? est.total : null;
  const leitura = leituraDasContagens({
    inconsistente,
    faltou: a.naoLocalizado,
    prateleira: prat?.encontrado ?? null,
    estoque: est === null ? null : { total: totalDoEstoque },
    hosNaAbertura: a.estoqueHos,
  });
  const decide = podeFazer(eu, 'divergencia.decidir');
  const chavePrat = useChaveDaAcao();
  const chaveEst = useChaveDaAcao();
  const [decidindo, setDecidindo] = useState(false);
  const [ajustar, setAjustar] = useState(false);
  const pendente = etapas.ajustes.some((x) => x.estado === 'PENDENTE_NO_HOS');

  const salvarPrateleira = async (n: number | null) => {
    if (n === null) return false;
    setAviso(null);
    const r = await conferirPrateleira(sessao, caso.id, {
      versao: caso.versao,
      chave: chavePrat(JSON.stringify([etapas.rodada, n, prat?.em ?? null])),
      encontrado: n,
      situacao: 'NA_PRATELEIRA',
      observacao: null,
      evidenciaId: null,
      liberar: true,
    });
    if (!tratar(r, 'A contagem da prateleira', true)) return false;
    await carregar();
    return true;
  };
  const salvarEstoque = async (n: number | null) => {
    setAviso(null);
    const r = await contarEstoque(sessao, caso.id, {
      chave: chaveEst(JSON.stringify([etapas.rodada, n, est?.em ?? null])),
      total: n,
    });
    if (!tratar(r, 'A contagem do estoque', true)) return false;
    await carregar();
    return true;
  };
  const decidir = async (erro: boolean) => {
    const resultado: ResultadoDaAuditoria = erro
      ? 'ERRO_BALCONISTA_CONFIRMADO'
      : inconsistente
        ? 'PROBLEMA_DADOS'
        : 'BALCONISTA_CORRETO';
    const desfecho: DesfechoDoCaso = inconsistente
      ? 'PROBLEMA_DADOS'
      : etapas.ajustes.some((x) => x.estado === 'CONFIRMADO')
        ? 'COM_AJUSTE'
        : 'SEM_AJUSTE';
    const estoqueTexto =
      est === null ? '—' : totalDoEstoque === null ? 'não verificado' : String(totalDoEstoque);
    setDecidindo(true);
    setAviso(null);
    const r = await concluir(sessao, caso.id, {
      resultado,
      desfecho,
      versao: caso.versao,
      observacao: `Prateleira ${numOuTraco(prat?.encontrado ?? null)} · estoque ${estoqueTexto} → ${erro ? 'foi erro do balconista' : 'não foi erro do balconista'}.`,
    });
    setDecidindo(false);
    const d = tratar(r, 'A decisão', true);
    if (!d) return;
    aoConcluir(d);
    await carregar();
  };

  const prontoParaDecidir = etapas.etapa === 'AJUSTE';
  return (
    <div className="cz-cartao cz-cartao-forte et-painel ds-cartao">
      <div className="cz-cartao-topo">
        <h2>Divergência</h2>
        {etapas.rodada > 1 && <span className="cz-nota">rodada {etapas.rodada}</span>}
      </div>
      <div className="ds-resumo">
        {inconsistente ? (
          <>
            <div>
              <span>Balconista viu na prateleira</span>
              <b>{numOuTraco(a.prateleira)}</b>
            </div>
            <div>
              <span>HOS dizia</span>
              <b>{a.achou}</b>
            </div>
          </>
        ) : (
          <>
            <div>
              <span>Sistema mandou repor</span>
              <b>{a.pedido}</b>
            </div>
            <div>
              <span>Balconista achou</span>
              <b>{a.achou}</b>
            </div>
            <div className="ds-faltou">
              <span>Faltou</span>
              <b>{a.naoLocalizado}</b>
            </div>
          </>
        )}
        <div>
          <span>HOS agora</span>
          <b>{numOuTraco(etapas.agora?.estoqueHos ?? null)}</b>
        </div>
      </div>

      <div className="ds-contagens">
        <Contagem
          numero={1}
          titulo="Prateleira"
          onde="em cima"
          registro={
            prat ? { valor: String(prat.encontrado), pessoa: prat.pessoa, em: prat.em } : null
          }
          pode={podeFazer(eu, 'divergencia.conferir_prateleira')}
          aoSalvar={salvarPrateleira}
        />
        <Contagem
          numero={2}
          titulo="Estoque"
          onde="embaixo · conte tudo o que tem lá"
          registro={
            est
              ? {
                  valor:
                    est.modo === 'TOTAL'
                      ? totalDoEstoque === null
                        ? 'Não verificou'
                        : String(totalDoEstoque)
                      : `${String(est.localizado)} de ${String(est.alvo)}`,
                  pessoa: est.pessoa,
                  em: est.em,
                }
              : null
          }
          pode={podeFazer(eu, 'divergencia.conferir_estoque')}
          naoVerificou
          aoSalvar={salvarEstoque}
        />
      </div>

      <div className="ds-decisao">
        <div className="ds-topo">
          <span className="ds-etapa">3</span>
          <div>
            <b>Decisão</b>
            <small>foi erro do balconista?</small>
          </div>
        </div>
        {!prontoParaDecidir ? (
          <p className="ds-esperando">
            {prat === null && est === null
              ? 'Esperando as contagens da prateleira e do estoque.'
              : prat === null
                ? 'Esperando a contagem da prateleira.'
                : 'Esperando a contagem do estoque.'}
          </p>
        ) : (
          <>
            {leitura.fisicoTotal !== null && (
              <p className="ds-total">
                Na loja: <b>{unidades(leitura.fisicoTotal)}</b> (prateleira + estoque) · HOS agora{' '}
                <b>{numOuTraco(etapas.agora?.estoqueHos ?? null)}</b>
              </p>
            )}
            {leitura.dica && <p className="ds-dica">{leitura.dica}</p>}
            {decide ? (
              <>
                <div className="ds-botoes">
                  <button
                    type="button"
                    className="cz-botao cz-botao-primario cz-botao-g"
                    disabled={!leitura.podeErro || decidindo || pendente}
                    onClick={() => void decidir(true)}
                  >
                    Foi erro do balconista
                  </button>
                  <button
                    type="button"
                    className="cz-botao cz-botao-contorno cz-botao-g"
                    disabled={decidindo || pendente}
                    onClick={() => void decidir(false)}
                  >
                    Não foi erro
                  </button>
                </div>
                {leitura.semErro && <small className="ds-motivo">{leitura.semErro}</small>}
                {pendente && (
                  <small className="ds-motivo">
                    Há um ajuste esperando no HOS: confirme ou cancele antes de decidir.
                  </small>
                )}
                <ListaDeAjustes {...p} />
                {!pendente && (
                  <button
                    type="button"
                    className="cz-link-botao ds-ajustar"
                    aria-expanded={ajustar}
                    onClick={() => setAjustar(!ajustar)}
                  >
                    {ajustar ? 'Fechar o ajuste do HOS' : 'Ajustar o estoque no HOS (opcional)'}
                  </button>
                )}
                {ajustar && !pendente && (
                  <NovoAjuste
                    caso={caso}
                    etapas={etapas}
                    sessao={sessao}
                    tratar={tratar}
                    carregar={carregar}
                    setAviso={setAviso}
                    inicial={leitura.fisicoTotal}
                    aoFeito={() => setAjustar(false)}
                  />
                )}
              </>
            ) : (
              <p className="ds-esperando">Quem decide já está vendo as duas contagens.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type PropsDoForm = Pick<PropsDaEtapa, 'caso' | 'sessao' | 'tratar' | 'carregar' | 'setAviso'> & {
  aoFeito: () => void;
};

function NovoAjuste({
  caso,
  etapas,
  sessao,
  tratar,
  carregar,
  setAviso,
  aoFeito,
  inicial,
}: PropsDoForm & { etapas: EtapasDoCaso; inicial: number | null }) {
  // Já vem com prateleira + estoque; quem ajusta só corrige se achou mais em outro lugar.
  const [fisico, setFisico] = useState<number | null>(inicial);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const chaveDe = useChaveDaAcao();
  const enviar = async () => {
    if (fisico === null) return;
    setEnviando(true);
    setAviso(null);
    const r = await registrarAjusteNoHos(sessao, caso.id, {
      versao: caso.versao,
      chave: chaveDe(JSON.stringify([caso.versao, fisico, motivo.trim()])),
      fisicoTotal: fisico,
      motivo: motivo.trim(),
    });
    setEnviando(false);
    const d = tratar(r, 'O ajuste', true);
    if (!d) return;
    aoFeito();
    setAviso({
      tipo: 'ok',
      titulo: `Ajuste de ${sinalDe(d.ajuste)} registrado como pendente no HOS`,
      texto: `Saldo do HOS lido agora: ${String(d.saldoHos)}. Lance no HOS e depois confirme aqui com a referência.`,
    });
    await carregar();
  };
  return (
    <div className="et-sub">
      <p className="et-aviso-fixo">
        Só informe a <b>contagem física COMPLETA</b> do produto (prateleira, estoque e todos os
        outros lugares) — nunca as unidades não localizadas da reposição. O ajuste fica pendente até
        alguém lançá-lo no HOS.
      </p>
      <label className="cz-rotulo">
        Contagem física total do produto
        <input
          className="et-numero"
          inputMode="numeric"
          placeholder="—"
          value={fisico === null ? '' : String(fisico)}
          onChange={(e) => {
            const v = soDigitos(e.target.value);
            setFisico(v === '' ? null : Number(v));
          }}
        />
        {etapas.abertura.estoqueHos !== null && (
          <small> Saldo do HOS na abertura: {etapas.abertura.estoqueHos}</small>
        )}
      </label>
      <label className="cz-rotulo">
        MOTIVO <span>· obrigatório, mínimo 5 caracteres</span>
        <textarea
          rows={2}
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="cz-botao cz-botao-primario cz-botao-m"
        disabled={fisico === null || motivo.trim().length < 5 || enviando}
        onClick={() => void enviar()}
      >
        {enviando ? 'Registrando…' : 'Registrar ajuste no HOS'}
      </button>
    </div>
  );
}

function ListaDeAjustes({ etapas, sessao, tratar, carregar, setAviso, eu }: PropsDaEtapa) {
  const decide = podeFazer(eu, 'divergencia.decidir');
  if (etapas.ajustes.length === 0) return null;
  return (
    <div className="et-ajustes">
      <h3>Ajustes no HOS</h3>
      {etapas.ajustes.map((a) => (
        <LinhaDeAjuste
          key={a.id}
          a={a}
          decide={decide}
          sessao={sessao}
          tratar={tratar}
          carregar={carregar}
          setAviso={setAviso}
        />
      ))}
    </div>
  );
}

function LinhaDeAjuste({
  a,
  decide,
  sessao,
  tratar,
  carregar,
  setAviso,
}: {
  a: EtapasDoCaso['ajustes'][number];
  decide: boolean;
} & Pick<PropsDaEtapa, 'sessao' | 'tratar' | 'carregar' | 'setAviso'>) {
  const [referencia, setReferencia] = useState('');
  const [enviando, setEnviando] = useState(false);
  const decidir = async (decisao: 'CONFIRMADO' | 'CANCELADO') => {
    setEnviando(true);
    setAviso(null);
    const r = await decidirAjuste(sessao, a.id, {
      decisao,
      referencia: decisao === 'CONFIRMADO' ? referencia.trim() : null,
    });
    setEnviando(false);
    if (!tratar(r, decisao === 'CONFIRMADO' ? 'A confirmação do ajuste' : 'O cancelamento', true))
      return;
    setAviso({
      tipo: 'ok',
      titulo: decisao === 'CONFIRMADO' ? 'Ajuste confirmado' : 'Ajuste cancelado',
    });
    await carregar();
  };
  return (
    <div className={`et-ajuste et-ajuste-${a.estado}`}>
      <div>
        <b>{ROTULO_DO_AJUSTE[a.estado]}</b> · {sinalDe(a.ajuste)} (físico {a.fisicoTotal}, saldo HOS{' '}
        {a.saldoHos})
        <small>
          {' '}
          · {primeiro(a.pessoa)} {quando(a.em)}
          {a.referencia ? ` · ref. ${a.referencia}` : ''}
        </small>
        <p className="cz-vazio">{a.motivo}</p>
      </div>
      {decide && a.estado === 'PENDENTE_NO_HOS' && (
        <div className="et-decisao">
          <input
            className="et-select"
            placeholder="Referência do lançamento no HOS"
            maxLength={120}
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
          />
          <button
            type="button"
            className="cz-botao cz-botao-primario cz-botao-m"
            disabled={enviando || referencia.trim().length < 1}
            onClick={() => void decidir('CONFIRMADO')}
          >
            Confirmar
          </button>
          <button
            type="button"
            className="cz-botao cz-botao-leve cz-botao-m"
            disabled={enviando}
            onClick={() => void decidir('CANCELADO')}
          >
            Cancelar ajuste
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Fora do AJUSTE: o administrador pode concluir sem as etapas
 * ------------------------------------------------------------------ */

export function ConcluirSemEtapas({ aoEscolher }: { aoEscolher: () => void }) {
  return (
    <details className="et-pular">
      <summary>Concluir sem as etapas</summary>
      <p>
        Encerra o caso agora, sem esperar a conferência que falta. Exige um motivo e fica no
        histórico.
      </p>
      <button type="button" className="cz-botao cz-botao-contorno cz-botao-m" onClick={aoEscolher}>
        Concluir sem as etapas…
      </button>
    </details>
  );
}
